import React, { useState } from "react"
import { Button, Alert, Form, FormGroup, Input, Label } from "reactstrap"
import { useLoading } from "helpers/custom_hooks"
import { UpdateCPSRequest } from "helpers/backend_helper"
import { displayMessage } from "helpers/misc_helpers"
import moment from "moment"

const CPSGoodsShipped = ({ entry, myUser, statusIndex, reload }) => {
  const setLoading = useLoading()
  const [isProcessing, setIsProcessing] = useState(false)
  const [trackingNumber, setTrackingNumber] = useState("")
  const [shippingService, setShippingService] = useState("")
  const [shippedQuantity, setShippedQuantity] = useState(entry?.quantity || "")

  // Determine if this step is active
  const isActive = entry?.statusIndex === 4 // After offer acceptance
  const isCompleted = entry?.statusIndex > 4

  // Check if user is the customer (can mark goods as shipped)
  const isCustomer = myUser?.company?.id === entry?.requestorCompanyID
  const canMarkShipped = isActive && !isCompleted && isCustomer

  const handleMarkShipped = async () => {
    if (!trackingNumber.trim()) {
      displayMessage("Please enter a tracking number", setError)
      return
    }

    const qty = parseInt(shippedQuantity, 10)
    if (!qty || qty <= 0) {
      displayMessage("Please enter a valid quantity to ship", setError)
      return
    }

    setIsProcessing(true)
    setLoading(true)
    try {
      const updatedEntry = {
        ...entry,
        // allow partial shipment: store how much was actually shipped now
        quantity: qty,
        trackingNumber: trackingNumber,
        shippingService: shippingService,
        dateShipped: new Date().toISOString(),
        statusIndex: 5, // Move to Goods Received status
      }

      await UpdateCPSRequest(updatedEntry)

      // Small delay to ensure backend has processed the update
      setTimeout(() => {
        reload()
      }, 500)
    } catch (error) {
      error?.response?.status == 400
        ? displayMessage(error.response.data, setError)
        : displayMessage("An unexpected error happened!", setError)
      console.log(error)
    }
    setIsProcessing(false)
    setLoading(false)
  }

  const getShippedDate = () => {
    if (entry?.dateShipped) {
      return moment(entry.dateShipped).format("DD.MM.YYYY")
    }
    return null
  }

  return (
    <li className={"event-list " + (isActive ? "active" : "")}>
      <div
        className={"event-timeline-dot " + (isActive ? "bx-fade-right" : "")}
      >
        <i className="bx bx-right-arrow-circle font-size-18"></i>
      </div>
      <div className="d-flex">
        <div className="flex-shrink-0 me-3">
          <h5 className="font-size-14">
            {isCompleted ? getShippedDate() : ""}{" "}
            <i className="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
          </h5>
        </div>
        <div className="flex-grow-1">
          <div>
            Goods Shipped{" "}
            {isActive
              ? " - waiting for customer to ship goods"
              : isCompleted
              ? " - goods shipped by customer"
              : ""}
          </div>
        </div>
      </div>

      {(isCompleted || entry?.trackingNumber) && (
        <div className="d-flex align-items-center mt-3">
          <div style={{ width: "45px" }}>
            <div className="avatar-sm">
              <span className="avatar-title rounded-circle bg-info bg-soft text-info font-size-24">
                <i className="bx bx-package" />
              </span>
            </div>
          </div>
          <div className="ms-4">
            <h5 className="font-size-14 mb-1">
              {isCompleted ? "Goods Shipped" : "Shipping Information"}
            </h5>
            {entry?.dateShipped && <small>Shipped on {getShippedDate()}</small>}
            {entry?.trackingNumber && (
              <div className="mt-1">
                <small className="text-muted">
                  <i className="bx bx-package me-1"></i>
                  Tracking: {entry.trackingNumber}
                </small>
              </div>
            )}
            {entry?.shippingService && (
              <div>
                <small className="text-muted">
                  <i className="bx bx-truck me-1"></i>
                  Service: {entry.shippingService}
                </small>
              </div>
            )}
          </div>
        </div>
      )}

      {canMarkShipped && (
        <div className="mt-3">
          <Alert color="info">
            <i className="bx bx-info-circle me-2"></i>
            Please provide tracking information when you ship the goods to the
            CPS Lab.
          </Alert>
          <Form>
            <FormGroup>
              <Label>Quantity to ship *</Label>
              <Input
                type="number"
                value={shippedQuantity}
                onChange={e => setShippedQuantity(e.target.value)}
                placeholder="Enter quantity to ship"
                min="1"
                max={entry?.quantity}
                disabled={isProcessing}
              />
              <div className="form-text text-muted">
                Requested quantity: {entry?.quantity}
              </div>
            </FormGroup>
            <FormGroup>
              <Label>Tracking Number *</Label>
              <Input
                type="text"
                value={trackingNumber}
                onChange={e => setTrackingNumber(e.target.value)}
                placeholder="Enter tracking number"
                disabled={isProcessing}
              />
            </FormGroup>
            <FormGroup>
              <Label>Shipping Service</Label>
              <Input
                type="text"
                value={shippingService}
                onChange={e => setShippingService(e.target.value)}
                placeholder="e.g., DHL, UPS, FedEx"
                disabled={isProcessing}
              />
            </FormGroup>
            <Button
              color="primary"
              size="sm"
              onClick={handleMarkShipped}
              disabled={isProcessing || !trackingNumber.trim()}
            >
              {isProcessing ? "Processing..." : "Mark as Shipped"}
            </Button>
          </Form>
        </div>
      )}
    </li>
  )
}

export default CPSGoodsShipped
